/**
 * @zendir/ui - Chart Presets
 *
 * Pre-configured chart components for common use cases.
 * Each preset wraps AstroChart with sensible defaults.
 */

import React, { forwardRef, memo } from "react";
import { AstroChart, AstroChartHandle } from "./AstroChart";
import type { AstroChartProps, SeriesData, AxisOptions } from "./types";
import { STATUS_COLORS, ASTRO_DATA_VIZ_COLORS } from "./theme";
import { useTheme } from "../../theme";

// Medium-soft status colors (readable, not too pale)
const SOFT_STATUS_COLORS = {
  normal: "#6ed86e",
  caution: "#ffd54f",
  serious: "#ffa726",
  critical: "#e57373",
  off: "#90a4ae",
  standby: "#4fc3f7",
};

// =============================================================================
// Shared Types
// =============================================================================

type PresetProps = Omit<AstroChartProps, "type">;

// =============================================================================
// Line Chart
// =============================================================================

export interface LineChartProps extends PresetProps {
  /** Enable smooth curves */
  smooth?: boolean;
  /** Show data points */
  showPoints?: boolean;
  /** Enable area fill */
  showArea?: boolean;
}

export const LineChart = memo(
  forwardRef<AstroChartHandle, LineChartProps>(function LineChart(
    {
      smooth = true,
      showPoints = false,
      showArea = false,
      series = [],
      ...props
    },
    ref,
  ) {
    const transformedSeries: SeriesData[] = series.map((s) => ({
      ...s,
      type: showArea ? "area" : "line",
      smooth,
      symbol: showPoints ? { type: "circle", size: 6 } : { type: "none" },
    }));

    return (
      <AstroChart
        ref={ref}
        type={showArea ? "area" : "line"}
        series={transformedSeries}
        tooltip={{ trigger: "axis", crosshair: "x" }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Area Chart
// =============================================================================

export interface AreaChartProps extends PresetProps {
  /** Stack areas */
  stacked?: boolean;
  /** Smooth curves */
  smooth?: boolean;
}

export const AreaChart = memo(
  forwardRef<AstroChartHandle, AreaChartProps>(function AreaChart(
    { stacked = false, smooth = true, series = [], ...props },
    ref,
  ) {
    const transformedSeries: SeriesData[] = series.map((s, _i) => ({
      ...s,
      type: "area",
      smooth,
      stack: stacked ? "total" : undefined,
      areaStyle: { opacity: stacked ? 0.8 : 0.3 },
    }));

    return (
      <AstroChart
        ref={ref}
        type="area"
        series={transformedSeries}
        tooltip={{ trigger: "axis", crosshair: "x" }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Bar Chart
// =============================================================================

export interface BarChartProps extends PresetProps {
  /** Stack bars */
  stacked?: boolean;
  /** Horizontal orientation */
  horizontal?: boolean;
  /** Bar category gap */
  categoryGap?: string;
}

export const BarChart = memo(
  forwardRef<AstroChartHandle, BarChartProps>(function BarChart(
    {
      stacked = false,
      horizontal = false,
      series = [],
      xAxis,
      yAxis,
      ...props
    },
    ref,
  ) {
    const transformedSeries: SeriesData[] = series.map((s) => ({
      ...s,
      type: "bar",
      stack: stacked ? "total" : undefined,
    }));

    // Swap axes for horizontal
    const finalXAxis: AxisOptions = horizontal
      ? { type: "value", ...(yAxis as AxisOptions) }
      : { type: "category", ...xAxis };

    const finalYAxis: AxisOptions = horizontal
      ? { type: "category", ...xAxis }
      : { type: "value", ...(yAxis as AxisOptions) };

    return (
      <AstroChart
        ref={ref}
        type="bar"
        series={transformedSeries}
        xAxis={finalXAxis}
        yAxis={finalYAxis}
        tooltip={{ trigger: "axis" }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Scatter Chart
// =============================================================================

export interface ScatterChartProps extends PresetProps {
  /** Symbol size */
  symbolSize?: number | ((value: number[]) => number);
  /** Show regression line */
  showRegression?: boolean;
}

export const ScatterChart = memo(
  forwardRef<AstroChartHandle, ScatterChartProps>(function ScatterChart(
    { symbolSize = 10, series = [], ...props },
    ref,
  ) {
    const transformedSeries: SeriesData[] = series.map((s) => ({
      ...s,
      type: "scatter",
      symbol: {
        type: "circle",
        size: typeof symbolSize === "number" ? symbolSize : 10,
      },
    }));

    return (
      <AstroChart
        ref={ref}
        type="scatter"
        series={transformedSeries}
        tooltip={{ trigger: "item" }}
        xAxis={{ type: "value" }}
        yAxis={{ type: "value" }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Pie Chart
// =============================================================================

export interface PieChartProps extends Omit<
  PresetProps,
  "xAxis" | "yAxis" | "zoom"
> {
  /** Show as donut */
  donut?: boolean;
  /** Inner radius for donut (percentage) */
  innerRadius?: string;
  /** Outer radius (percentage) */
  outerRadius?: string;
  /** Show labels */
  showLabels?: boolean;
  /** Center content (for donut) */
  centerContent?: React.ReactNode;
}

export const PieChart = memo(
  forwardRef<AstroChartHandle, PieChartProps>(function PieChart(
    {
      donut = false,
      innerRadius = "40%",
      outerRadius = "70%",
      showLabels = true,
      series = [],
      ...props
    },
    ref,
  ) {
    const transformedSeries: SeriesData[] = series.map((s) => ({
      ...s,
      type: donut ? "donut" : "pie",
    }));

    return (
      <AstroChart
        ref={ref}
        type={donut ? "donut" : "pie"}
        series={transformedSeries}
        tooltip={{ trigger: "item" }}
        echartsOptions={{
          series: [
            {
              radius: donut ? [innerRadius, outerRadius] : outerRadius,
              label: {
                show: showLabels,
                formatter: "{b}: {d}%",
              },
            },
          ],
        }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Donut Chart
// =============================================================================

export const DonutChart = memo(
  forwardRef<AstroChartHandle, PieChartProps>(function DonutChart(props, ref) {
    return <PieChart ref={ref} donut {...props} />;
  }),
);

// =============================================================================
// Gauge Chart
// =============================================================================

export interface GaugeChartProps extends Omit<
  PresetProps,
  "xAxis" | "yAxis" | "zoom" | "series"
> {
  /** Current value */
  value: number;
  /** Minimum value */
  min?: number;
  /** Maximum value */
  max?: number;
  /** Unit label */
  unit?: string;
  /** Title label */
  label?: string;
  /** Threshold colors */
  thresholds?: {
    value: number;
    color: string;
  }[];
  /** Show progress arc */
  progress?: boolean;
  /** Show clock-style pointer (needle). Set false for arc + value only. */
  showPointer?: boolean;
}

export const GaugeChart = memo(
  forwardRef<AstroChartHandle, GaugeChartProps>(function GaugeChart(
    {
      value,
      min = 0,
      max = 100,
      unit = "%",
      label,
      thresholds,
      progress = true,
      showPointer = true,
      width = 220,
      height = 220,
      ...props
    },
    ref,
  ) {
    const { tokens } = useTheme();
    const range = max - min;
    const safeRange = range === 0 ? 1 : range;
    const clampedValue = Math.max(min, Math.min(max, value));
    const widthPx =
      typeof width === "number"
        ? width
        : typeof height === "number"
          ? height
          : 220;
    const heightPx = typeof height === "number" ? height : 220;
    const gaugeSize = Math.max(120, Math.min(widthPx, heightPx));
    const compactGauge = gaugeSize < 170;
    const arcWidth = Math.max(8, Math.round(gaugeSize * 0.07));
    const valueFontSize = Math.max(16, Math.round(gaugeSize * 0.15));
    const labelFontSize = Math.max(10, Math.round(gaugeSize * 0.07));
    const unitSuffix = unit?.trim() ?? "";
    const longUnit = unitSuffix.length > 4;

    const series: SeriesData[] = [
      {
        id: "gauge",
        name: label || "Value",
        type: "gauge",
        data: [{ x: 0, y: clampedValue }],
      },
    ];

    // Build color stops from thresholds
    const normalizedThresholds = [...(thresholds ?? [])]
      .filter((t) => Number.isFinite(t.value))
      .sort((a, b) => a.value - b.value);

    const colorStops = normalizedThresholds.map(
      (t) =>
        [Math.max(0, Math.min(1, (t.value - min) / safeRange)), t.color] as [
          number,
          string,
        ],
    );

    if (colorStops.length === 0) {
      colorStops.push(
        [0.3, SOFT_STATUS_COLORS.normal],
        [0.7, SOFT_STATUS_COLORS.caution],
        [1, SOFT_STATUS_COLORS.critical],
      );
    } else if (colorStops[colorStops.length - 1][0] < 1) {
      colorStops.push([1, colorStops[colorStops.length - 1][1]]);
    }

    const activeColor =
      [...colorStops]
        .reverse()
        .find(([stop]) => stop <= (clampedValue - min) / safeRange)?.[1] ??
      SOFT_STATUS_COLORS.normal;

    return (
      <AstroChart
        ref={ref}
        type="gauge"
        series={series}
        width={width}
        height={height}
        echartsOptions={{
          series: [
            {
              type: "gauge",
              min,
              max,
              progress: {
                show: progress,
                width: arcWidth,
                roundCap: true,
              },
              axisLine: {
                lineStyle: {
                  width: arcWidth,
                  color: colorStops,
                },
              },
              axisTick: { show: false },
              splitLine: { show: false },
              axisLabel: { show: false },
              pointer: {
                show: showPointer,
                length: compactGauge ? "56%" : "60%",
                width: Math.max(3, Math.round(arcWidth * 0.38)),
              },
              title: {
                show: !!label,
                offsetCenter: [0, compactGauge ? "73%" : "78%"],
                fontSize: labelFontSize,
                color: tokens.colors.text.secondary,
              },
              detail: {
                valueAnimation: true,
                formatter: (currentValue: number) => {
                  const rounded = Math.round(currentValue);
                  if (!unitSuffix) return `${rounded}`;
                  if (longUnit) return `${rounded}\n{unit|${unitSuffix}}`;
                  return `${rounded}${unitSuffix}`;
                },
                rich: {
                  unit: {
                    color: tokens.colors.text.secondary,
                    fontSize: Math.max(10, Math.round(valueFontSize * 0.48)),
                    fontWeight: 500,
                    lineHeight: Math.round(valueFontSize * 0.78),
                  },
                },
                lineHeight: Math.round(valueFontSize * 1.05),
                fontSize: valueFontSize,
                fontWeight: 700,
                offsetCenter: [0, compactGauge ? "10%" : "14%"],
                color: activeColor,
              },
              data: [{ value: clampedValue, name: label }],
            },
          ],
        }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Radar Chart
// =============================================================================

export interface RadarChartProps extends Omit<
  PresetProps,
  "xAxis" | "yAxis" | "zoom"
> {
  /** Indicator definitions */
  indicators: {
    name: string;
    max: number;
    min?: number;
  }[];
  /** Fill area */
  areaStyle?: boolean;
}

export const RadarChart = memo(
  forwardRef<AstroChartHandle, RadarChartProps>(function RadarChart(
    { indicators, areaStyle = true, series = [], ...props },
    ref,
  ) {
    // Extract values from data - support both [{x,y}] and [number] formats
    const extractValues = (data: unknown[]): number[] => {
      if (!data || data.length === 0) return [];
      const first = data[0];
      if (typeof first === "number") return data as number[];
      if (typeof first === "object" && first !== null && "y" in first) {
        return (data as Array<{ y: number }>).map((d) => d.y);
      }
      return [];
    };

    return (
      <AstroChart
        ref={ref}
        type="radar"
        series={[]} // Empty - we provide series via echartsOptions
        tooltip={{ trigger: "item" }}
        echartsOptions={{
          radar: {
            indicator: indicators,
            shape: "polygon",
          },
          series: series.map((s) => ({
            type: "radar",
            name: s.name,
            data: [
              {
                value: extractValues(s.data as unknown[]),
                name: s.name,
              },
            ],
            areaStyle: areaStyle ? { opacity: 0.3 } : undefined,
          })),
        }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Heatmap Chart
// =============================================================================

export interface HeatmapChartProps extends PresetProps {
  /** X-axis categories */
  xCategories: string[];
  /** Y-axis categories */
  yCategories: string[];
  /** Value range */
  valueRange?: [number, number];
  /** Show value labels */
  showLabels?: boolean;
}

export const HeatmapChart = memo(
  forwardRef<AstroChartHandle, HeatmapChartProps>(function HeatmapChart(
    {
      xCategories,
      yCategories,
      valueRange = [0, 100],
      showLabels = true,
      series = [],
      ...props
    },
    ref,
  ) {
    // Transform data to heatmap format if needed
    const heatmapData = series[0]?.data as unknown[];

    return (
      <AstroChart
        ref={ref}
        type="heatmap"
        series={series}
        xAxis={{ type: "category" }}
        yAxis={{ type: "category" }}
        tooltip={{ trigger: "item" }}
        echartsOptions={{
          xAxis: { data: xCategories },
          yAxis: { data: yCategories },
          visualMap: {
            min: valueRange[0],
            max: valueRange[1],
            calculable: true,
            orient: "horizontal",
            left: "center",
            bottom: "5%",
          },
          series: [
            {
              type: "heatmap",
              data: heatmapData as [number, number, number][],
              label: {
                show: showLabels,
              },
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowColor: "rgba(0, 0, 0, 0.5)",
                },
              },
            },
          ],
        }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Time Series Chart (optimized for streaming data)
// =============================================================================

export interface TimeSeriesChartProps extends PresetProps {
  /** Time window in milliseconds */
  timeWindow?: number;
  /** Maximum data points */
  maxPoints?: number;
  /** Auto-scroll to latest data */
  autoScroll?: boolean;
  /** Show current values */
  showCurrentValues?: boolean;
}

export const TimeSeriesChart = memo(
  forwardRef<AstroChartHandle, TimeSeriesChartProps>(function TimeSeriesChart(
    {
      timeWindow,
      maxPoints = 1000,
      autoScroll = true,
      showCurrentValues: _showCurrentValues = true,
      series = [],
      ...props
    },
    ref,
  ) {
    const xAxis: AxisOptions = {
      type: "time",
      ...(timeWindow
        ? {
            min: "dataMin",
            max: "dataMax",
          }
        : {}),
    };

    return (
      <AstroChart
        ref={ref}
        type="line"
        series={series.map((s) => ({
          ...s,
          smooth: true,
          symbol: { type: "none" },
        }))}
        xAxis={xAxis}
        tooltip={{
          trigger: "axis",
          crosshair: "x",
        }}
        zoom={
          autoScroll
            ? undefined
            : {
                enabled: true,
                type: "both",
                axis: "x",
              }
        }
        realTime={{
          enabled: true,
          maxPoints,
          timeWindow,
          shift: autoScroll,
        }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Dual Axis Chart
// =============================================================================

export interface DualAxisChartProps extends PresetProps {
  /** Left Y-axis options */
  leftAxis?: AxisOptions;
  /** Right Y-axis options */
  rightAxis?: AxisOptions;
}

export const DualAxisChart = memo(
  forwardRef<AstroChartHandle, DualAxisChartProps>(function DualAxisChart(
    { leftAxis, rightAxis, series = [], ...props },
    ref,
  ) {
    // Assign series to axes based on yAxisIndex
    const transformedSeries: SeriesData[] = series.map((s, i) => ({
      ...s,
      yAxisIndex: s.yAxisIndex ?? i % 2, // Alternate by default
    }));

    return (
      <AstroChart
        ref={ref}
        type="line"
        series={transformedSeries}
        yAxis={[
          { position: "left", ...leftAxis },
          { position: "right", ...rightAxis },
        ]}
        tooltip={{ trigger: "axis", crosshair: "x" }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Stacked Area Chart
// =============================================================================

export const StackedAreaChart = memo(
  forwardRef<AstroChartHandle, AreaChartProps>(
    function StackedAreaChart(props, ref) {
      return <AreaChart ref={ref} stacked {...props} />;
    },
  ),
);

// =============================================================================
// Stacked Bar Chart
// =============================================================================

export const StackedBarChart = memo(
  forwardRef<AstroChartHandle, BarChartProps>(
    function StackedBarChart(props, ref) {
      return <BarChart ref={ref} stacked {...props} />;
    },
  ),
);

// =============================================================================
// Bubble Chart (AstroUXD compliant)
// =============================================================================

export interface BubbleChartProps extends PresetProps {
  /** Min bubble size (px) */
  minSize?: number;
  /** Max bubble size (px) */
  maxSize?: number;
  /** Show size legend */
  showSizeLegend?: boolean;
}

export const BubbleChart = memo(
  forwardRef<AstroChartHandle, BubbleChartProps>(function BubbleChart(
    {
      minSize = 10,
      maxSize = 60,
      showSizeLegend = true,
      series = [],
      ...props
    },
    ref,
  ) {
    // Transform series for bubble (scatter with symbolSize)
    const transformedSeries: SeriesData[] = series.map((s) => ({
      ...s,
      type: "scatter" as const,
    }));

    return (
      <AstroChart
        ref={ref}
        type="scatter"
        series={transformedSeries}
        tooltip={{ trigger: "item" }}
        echartsOptions={{
          series: series.map((s, _idx) => {
            const data = s.data as Array<
              | { x: number; y: number; size?: number; label?: string }
              | [number, number, number]
            >;
            return {
              type: "scatter",
              name: s.name,
              data: data.map((d) => {
                if (Array.isArray(d)) return d;
                return [d.x, d.y, (d as { size?: number }).size ?? 10];
              }),
              symbolSize: (val: number[]) => {
                const size = val[2] ?? minSize;
                return Math.max(minSize, Math.min(maxSize, size));
              },
            };
          }),
          visualMap: showSizeLegend
            ? {
                show: true,
                dimension: 2,
                min: 0,
                max: 100,
                inRange: { symbolSize: [minSize, maxSize] },
                right: 10,
                top: "center",
              }
            : undefined,
        }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Histogram Chart (AstroUXD compliant)
// =============================================================================

export interface HistogramChartProps extends PresetProps {
  /** Number of bins (auto if not specified) */
  bins?: number;
  /** Show distribution curve overlay */
  showCurve?: boolean;
}

export const HistogramChart = memo(
  forwardRef<AstroChartHandle, HistogramChartProps>(function HistogramChart(
    {
      bins: _bins = 20,
      showCurve: _showCurve = false,
      series = [],
      xAxis,
      ...props
    },
    ref,
  ) {
    // Transform data into histogram bins
    const transformedSeries: SeriesData[] = series.map((s) => ({
      ...s,
      type: "bar" as const,
    }));

    return (
      <AstroChart
        ref={ref}
        type="bar"
        series={transformedSeries}
        xAxis={{ type: "category", ...xAxis }}
        yAxis={{ type: "value", name: "Frequency" }}
        tooltip={{ trigger: "axis" }}
        echartsOptions={{
          series: series.map((s) => ({
            type: "bar",
            name: s.name,
            data: s.data,
            barWidth: "90%",
            itemStyle: { borderRadius: [2, 2, 0, 0] },
          })) as import("echarts").SeriesOption[],
        }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Sankey Diagram (Power/Data Flow)
// =============================================================================

export interface SankeyChartProps extends Omit<PresetProps, "series"> {
  /** Node definitions */
  nodes: { name: string; itemStyle?: { color?: string } }[];
  /** Link definitions */
  links: { source: string; target: string; value: number }[];
  /** Orient vertical or horizontal */
  orient?: "horizontal" | "vertical";
}

export const SankeyChart = memo(
  forwardRef<AstroChartHandle, SankeyChartProps>(function SankeyChart(
    { nodes = [], links = [], orient = "horizontal", ...props },
    ref,
  ) {
    const { tokens } = useTheme();

    return (
      <AstroChart
        ref={ref}
        type="sankey"
        series={[]}
        echartsOptions={{
          tooltip: {
            trigger: "item",
            triggerOn: "mousemove",
          },
          series: [
            {
              type: "sankey",
              orient,
              layoutIterations: 32,
              emphasis: { focus: "adjacency" },
              nodeAlign: "justify",
              lineStyle: { color: "gradient", curveness: 0.5 },
              data: nodes,
              links,
              label: {
                color: tokens.colors.text.primary,
                fontSize: 12,
              },
            },
          ],
        }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Treemap (Storage/Allocation Visualization)
// AstroUXDS compliant hierarchical visualization
// =============================================================================

export interface TreemapDataNode {
  name: string;
  value?: number;
  children?: TreemapDataNode[];
  itemStyle?: { color?: string };
}

export interface TreemapChartProps extends Omit<PresetProps, "series"> {
  /** Hierarchical data */
  data: TreemapDataNode[];
  /** Show breadcrumb navigation */
  showBreadcrumb?: boolean;
  /** Leaf depth to show */
  leafDepth?: number;
}

export const TreemapChart = memo(
  forwardRef<AstroChartHandle, TreemapChartProps>(function TreemapChart(
    { data = [], showBreadcrumb = true, leafDepth = 2, ...props },
    ref,
  ) {
    const { tokens } = useTheme();

    return (
      <AstroChart
        ref={ref}
        type="treemap"
        series={[]}
        echartsOptions={{
          tooltip: {
            formatter: ((info: any) => {
              const value = info.value;
              const name = info.name;
              return `${name}: ${typeof value === "number" ? value.toLocaleString() : value}`;
            }) as unknown as string,
          },
          series: [
            {
              type: "treemap",
              data,
              leafDepth,
              roam: false,
              // AstroUXDS compliant breadcrumb navigation
              breadcrumb: {
                show: showBreadcrumb,
                bottom: 10,
                itemStyle: { color: "rgba(100, 116, 139, 0.6)" }, // Subtle grey
              },
              label: {
                show: true,
                formatter: "{b}",
                color: tokens.colors.text.primary,
                fontSize: 12,
              },
              upperLabel: {
                show: true,
                height: 30,
                color: tokens.colors.text.secondary,
              },
              // AstroUXDS compliant borders - subtle grey
              itemStyle: {
                borderColor: "rgba(100, 116, 139, 0.2)",
                borderWidth: 2,
                gapWidth: 2,
              },
              levels: [
                {
                  itemStyle: {
                    borderColor: "rgba(100, 116, 139, 0.3)",
                    borderWidth: 4,
                    gapWidth: 4,
                  },
                  upperLabel: { show: false },
                },
                {
                  itemStyle: {
                    borderColor: "rgba(100, 116, 139, 0.25)",
                    borderWidth: 2,
                    gapWidth: 2,
                  },
                  emphasis: {
                    itemStyle: { borderColor: ASTRO_DATA_VIZ_COLORS.mixed[0] },
                  }, // Zendir Electric on hover
                },
                {
                  colorSaturation: [0.35, 0.5],
                  itemStyle: {
                    borderColor: "rgba(62, 60, 255, 0.3)",
                    borderWidth: 1,
                  }, // Zendir Electric subtle
                },
              ],
            },
          ],
        }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Sunburst Chart (Hierarchical Status)
// =============================================================================

export interface SunburstDataNode {
  name: string;
  value?: number;
  children?: SunburstDataNode[];
  itemStyle?: { color?: string };
}

export interface SunburstChartProps extends Omit<PresetProps, "series"> {
  /** Hierarchical data */
  data: SunburstDataNode[];
  /** Inner radius (percentage or pixels) */
  innerRadius?: string | number;
  /** Outer radius (percentage or pixels) */
  outerRadius?: string | number;
}

export const SunburstChart = memo(
  forwardRef<AstroChartHandle, SunburstChartProps>(function SunburstChart(
    { data = [], innerRadius = "15%", outerRadius = "80%", ...props },
    ref,
  ) {
    const { tokens } = useTheme();

    return (
      <AstroChart
        ref={ref}
        type="sunburst"
        series={[]}
        echartsOptions={{
          tooltip: {
            trigger: "item",
          },
          // Zendir-branded sunburst visualization
          series: [
            {
              type: "sunburst",
              data,
              radius: [innerRadius, outerRadius],
              emphasis: { focus: "ancestor" },
              label: {
                rotate: "radial",
                color: tokens.colors.text.secondary,
                fontSize: 11,
              },
              itemStyle: {
                borderRadius: 4,
                borderWidth: 2,
                borderColor: "rgba(62, 60, 255, 0.25)", // Zendir Electric subtle border
              },
              levels: [
                {},
                { r0: "15%", r: "40%", label: { rotate: "tangential" } },
                { r0: "40%", r: "65%" },
                {
                  r0: "65%",
                  r: "80%",
                  label: { position: "outside", fontSize: 10 },
                },
              ],
            },
          ],
        }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Parallel Coordinates (Multi-Parameter Analysis)
// =============================================================================

export interface ParallelAxisDef {
  dim: number;
  name: string;
  min?: number;
  max?: number;
  type?: "value" | "category";
  data?: string[];
}

export interface ParallelChartProps extends Omit<PresetProps, "series"> {
  /** Axis definitions */
  axes: ParallelAxisDef[];
  /** Data rows (each row is values for each axis) */
  data: (number | string)[][];
  /** Line style */
  lineStyle?: { opacity?: number; width?: number };
}

export const ParallelChart = memo(
  forwardRef<AstroChartHandle, ParallelChartProps>(function ParallelChart(
    {
      axes = [],
      data = [],
      lineStyle = { opacity: 0.5, width: 1.5 },
      ...props
    },
    ref,
  ) {
    const { tokens } = useTheme();

    return (
      <AstroChart
        ref={ref}
        type="line"
        series={[]}
        // AstroUXDS compliant parallel coordinates
        echartsOptions={{
          parallelAxis: axes.map((axis) => ({
            dim: axis.dim,
            name: axis.name,
            min: axis.min,
            max: axis.max,
            type: axis.type || "value",
            data: axis.data,
            nameTextStyle: { color: tokens.colors.text.secondary },
            axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.3)" } },
            axisTick: { lineStyle: { color: "rgba(100, 116, 139, 0.3)" } },
            axisLabel: { color: tokens.colors.text.tertiary },
          })),
          parallel: {
            left: 60,
            right: 60,
            bottom: 60,
            top: 60,
            parallelAxisDefault: {
              type: "value",
              nameLocation: "end",
              nameGap: 20,
              axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.3)" } },
              axisTick: { lineStyle: { color: "rgba(100, 116, 139, 0.3)" } },
              splitLine: { show: false },
              axisLabel: { color: tokens.colors.text.tertiary },
            },
          },
          series: [
            {
              type: "parallel",
              lineStyle: {
                ...lineStyle,
                color: ASTRO_DATA_VIZ_COLORS.mixed[0], // Zendir Electric
              },
              data: data.map((row) => ({ value: row })),
              emphasis: {
                lineStyle: { width: 3, opacity: 1 },
              },
            },
          ],
        }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Candlestick Chart (Financial/Orbital Maneuver Windows)
// =============================================================================

export interface CandlestickDataPoint {
  time: number | string;
  open: number;
  close: number;
  low: number;
  high: number;
}

export interface CandlestickChartProps extends Omit<PresetProps, "series"> {
  /** OHLC data */
  data: CandlestickDataPoint[];
  /** Show volume bars */
  showVolume?: boolean;
}

export const CandlestickChart = memo(
  forwardRef<AstroChartHandle, CandlestickChartProps>(function CandlestickChart(
    { data = [], showVolume: _showVolume = false, ...props },
    ref,
  ) {
    const { tokens } = useTheme();
    const categoryData = data.map((d) => d.time);
    const ohlcData = data.map((d) => [d.open, d.close, d.low, d.high]);

    return (
      <AstroChart
        ref={ref}
        type="candlestick"
        series={[]}
        // Candlestick uses AstroUXDS status colors for semantic meaning
        // Green (normal) = bullish/up, Red (critical) = bearish/down
        echartsOptions={{
          tooltip: {
            trigger: "axis",
            axisPointer: { type: "cross" },
          },
          xAxis: {
            type: "category",
            data: categoryData,
            axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.3)" } },
            axisLabel: { color: tokens.colors.text.secondary },
          },
          yAxis: {
            type: "value",
            scale: true,
            splitLine: { lineStyle: { color: "rgba(100, 116, 139, 0.15)" } },
            axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.3)" } },
            axisLabel: { color: tokens.colors.text.secondary },
          },
          series: [
            {
              type: "candlestick",
              data: ohlcData,
              itemStyle: {
                color: SOFT_STATUS_COLORS.normal, // AstroUXDS green (up/bullish)
                color0: SOFT_STATUS_COLORS.critical, // AstroUXDS red (down/bearish)
                borderColor: SOFT_STATUS_COLORS.normal,
                borderColor0: SOFT_STATUS_COLORS.critical,
              },
            },
          ],
          dataZoom: [
            { type: "inside", start: 0, end: 100 },
            { show: true, type: "slider", bottom: 10 },
          ],
        }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Graph/Network Chart (Subsystem Dependencies)
// =============================================================================

export interface GraphNode {
  id: string;
  name: string;
  value?: number;
  category?: number;
  symbolSize?: number;
  x?: number;
  y?: number;
  itemStyle?: { color?: string };
}

export interface GraphLink {
  source: string;
  target: string;
  value?: number;
  lineStyle?: { width?: number; color?: string };
}

export interface GraphCategory {
  name: string;
  itemStyle?: { color?: string };
}

export interface GraphChartProps extends Omit<PresetProps, "series"> {
  /** Node definitions */
  nodes: GraphNode[];
  /** Link definitions */
  links: GraphLink[];
  /** Category definitions */
  categories?: GraphCategory[];
  /** Layout type */
  layout?: "force" | "circular" | "none";
}

export const GraphChart = memo(
  forwardRef<AstroChartHandle, GraphChartProps>(function GraphChart(
    { nodes = [], links = [], categories = [], layout = "force", ...props },
    ref,
  ) {
    const { tokens } = useTheme();

    return (
      <AstroChart
        ref={ref}
        type="scatter"
        series={[]}
        // Zendir-branded network/graph visualization
        echartsOptions={{
          tooltip: {},
          legend:
            categories.length > 0
              ? {
                  data: categories.map((c) => c.name),
                  textStyle: { color: tokens.colors.text.primary },
                }
              : undefined,
          series: [
            {
              type: "graph",
              layout,
              data: nodes.map((n) => ({
                ...n,
                symbolSize: n.symbolSize || 30,
              })),
              links,
              categories,
              roam: true,
              label: {
                show: true,
                position: "right",
                formatter: "{b}",
                color: tokens.colors.text.primary,
                fontSize: 11,
              },
              lineStyle: {
                color: "source",
                curveness: 0.3,
                opacity: 0.6,
              },
              emphasis: {
                focus: "adjacency",
                lineStyle: { width: 3 },
              },
              force:
                layout === "force"
                  ? {
                      repulsion: 200,
                      edgeLength: [50, 150],
                      gravity: 0.1,
                    }
                  : undefined,
            },
          ],
        }}
        {...props}
      />
    );
  }),
);

// =============================================================================
// Box Plot Chart
// =============================================================================

export interface BoxPlotDataPoint {
  /** Category name (x-axis label) */
  name: string;
  /** Raw data values for computing boxplot statistics */
  values: number[];
}

export interface BoxPlotChartProps extends Omit<PresetProps, "series"> {
  /** Box plot data - each item contains a category name and array of values */
  data: BoxPlotDataPoint[];
  /** Show outliers as scatter points */
  showOutliers?: boolean;
  /** Custom colors for each box (uses theme palette if not provided) */
  colors?: string[];
  /** Horizontal orientation (default is vertical) */
  horizontal?: boolean;
}

/**
 * Calculate boxplot statistics from raw values
 */
function calculateBoxplotStats(
  values: number[],
): [number, number, number, number, number] {
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;

  const min = sorted[0];
  const max = sorted[n - 1];
  const median =
    n % 2 === 0
      ? (sorted[n / 2 - 1] + sorted[n / 2]) / 2
      : sorted[Math.floor(n / 2)];

  const q1Index = Math.floor(n * 0.25);
  const q3Index = Math.floor(n * 0.75);
  const q1 = sorted[q1Index];
  const q3 = sorted[q3Index];

  // IQR-based whiskers
  const iqr = q3 - q1;
  const lowerWhisker = Math.max(min, q1 - 1.5 * iqr);
  const upperWhisker = Math.min(max, q3 + 1.5 * iqr);

  return [lowerWhisker, q1, median, q3, upperWhisker];
}

/**
 * BoxPlotChart - Statistical distribution visualization
 *
 * Shows distribution of data through quartiles (box) and whiskers (range).
 * Uses theme-aware colors from the data visualization palette.
 *
 * @example
 * ```tsx
 * <BoxPlotChart
 *   title="Score Distribution"
 *   data={[
 *     { name: 'Team A', values: [65, 72, 84, 91, 55, 88, 76] },
 *     { name: 'Team B', values: [78, 82, 95, 88, 70, 92, 85] },
 *   ]}
 * />
 * ```
 */
export const BoxPlotChart = memo(
  forwardRef<AstroChartHandle, BoxPlotChartProps>(function BoxPlotChart(
    { data = [], showOutliers = true, colors, horizontal = false, ...props },
    ref,
  ) {
    const { tokens } = useTheme();

    // Calculate boxplot statistics for each category
    const categories = data.map((d) => d.name);
    const boxplotData = data.map((d, index) => {
      const stats = calculateBoxplotStats(d.values);
      return {
        value: stats,
        itemStyle: colors?.[index]
          ? {
              color: colors[index],
              borderColor: colors[index],
            }
          : undefined,
      };
    });

    // Find outliers for each category
    const outliers: Array<[number, number]> = [];
    if (showOutliers) {
      data.forEach((d, catIndex) => {
        const sorted = [...d.values].sort((a, b) => a - b);
        const n = sorted.length;
        const q1Index = Math.floor(n * 0.25);
        const q3Index = Math.floor(n * 0.75);
        const q1 = sorted[q1Index];
        const q3 = sorted[q3Index];
        const iqr = q3 - q1;
        const lowerBound = q1 - 1.5 * iqr;
        const upperBound = q3 + 1.5 * iqr;

        d.values.forEach((v) => {
          if (v < lowerBound || v > upperBound) {
            outliers.push([catIndex, v]);
          }
        });
      });
    }

    const categoryAxis = {
      type: "category" as const,
      data: categories,
      boundaryGap: true,
      axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.3)" } },
      axisLabel: { color: tokens.colors.text.secondary, fontSize: 12 },
      splitArea: { show: false },
    };

    const valueAxis = {
      type: "value" as const,
      splitLine: { lineStyle: { color: "rgba(100, 116, 139, 0.15)" } },
      axisLine: { lineStyle: { color: "rgba(100, 116, 139, 0.3)" } },
      axisLabel: { color: tokens.colors.text.secondary },
    };

    return (
      <AstroChart
        ref={ref}
        type="boxplot"
        series={[]}
        echartsOptions={{
          tooltip: {
            trigger: "item",
            axisPointer: { type: "shadow" },
            formatter: (params: any) => {
              if (params.seriesType === "boxplot") {
                const value = params.value;
                return `
                  <div style="font-weight: 600; margin-bottom: 4px;">${params.name}</div>
                  <div>Upper: ${value[5]?.toFixed(1) ?? value[4]?.toFixed(1)}</div>
                  <div>Q3: ${value[4]?.toFixed(1) ?? value[3]?.toFixed(1)}</div>
                  <div>Median: ${value[3]?.toFixed(1) ?? value[2]?.toFixed(1)}</div>
                  <div>Q1: ${value[2]?.toFixed(1) ?? value[1]?.toFixed(1)}</div>
                  <div>Lower: ${value[1]?.toFixed(1) ?? value[0]?.toFixed(1)}</div>
                `;
              }
              return `Outlier: ${params.value[1]}`;
            },
          },
          grid: {
            left: "10%",
            right: "10%",
            bottom: "15%",
            top: "15%",
          },
          xAxis: horizontal ? valueAxis : categoryAxis,
          yAxis: horizontal ? categoryAxis : valueAxis,
          series: [
            {
              name: "boxplot",
              type: "boxplot" as const,
              data: boxplotData,
              itemStyle: {
                borderWidth: 2,
              },
              emphasis: {
                itemStyle: {
                  borderWidth: 3,
                },
              },
            },
            ...(showOutliers && outliers.length > 0
              ? [
                  {
                    name: "outlier",
                    type: "scatter" as const,
                    data: outliers,
                    symbolSize: 8,
                    itemStyle: {
                      color: SOFT_STATUS_COLORS.caution,
                    },
                  },
                ]
              : []),
          ],
        }}
        {...props}
      />
    );
  }),
);
